import axios from 'axios'
import { MessageBox } from 'element-ui'
import { apiServer, ecsServer, strApiServer, decoderServer } from '@/config'
import storejs from 'store'

const api = {
  getPrivileges() {
    return axios.get(`${apiServer}/user/privileges`)
  },
  getTarInfo({ tarId }) {
    return axios.get(`${apiServer}/detectionData/desc/${tarId}`)
  },
  getCameraList({ tarUrl }) {
    return axios.get(
      `${ecsServer}/tar-preview/camera-list?tar-file=${tarUrl}`,
      {
        withCredentials: false,
      },
    )
  },
  getCameraData({ tarUrl, cameraId, bitrateInMbps }) {
    return axios.get(
      `${ecsServer}/tar-preview/camera-video?tar-file=${tarUrl}&camera-id=${cameraId}&bitrate-in-mbps=${bitrateInMbps}`,
      {
        withCredentials: false,
      },
    )
  },
  getLabelList({ tarId, cameraId }) {
    return axios.get(
      `${apiServer}/labeling/tars/${tarId}/cameras/${cameraId}/self`,
    )
  },
  putLabelList({ tarId, cameraId, data }) {
    return axios.put(
      `${apiServer}/labeling/tars/${tarId}/cameras/${cameraId}/self`,
      data,
    )
  },
  getLabelers({ tarId, cameraId }) {
    return axios.get(
      `${apiServer}/labeling/tars/${tarId}/cameras/${cameraId}/labelers`,
    )
  },
  patchLabel({ tarId, cameraId, labelerName, data }) {
    return axios.patch(
      `${apiServer}/labeling/tars/${tarId}/cameras/${cameraId}/labelers/${labelerName}`,
      data,
    )
  },
  getTarList(options) {
    return axios.get(`${apiServer}/labeling/tars`, {
      params: options,
    })
  },
  getHistory({ tarId, cameraId }) {
    return axios.get(
      `${apiServer}/labeling/tars/${tarId}/cameras/${cameraId}/reviews`,
    )
  },
  getFactory() {
    return axios.get(`${apiServer}/labeling/factory-list`)
  },
  getAlias() {
    return axios.get(`${apiServer}/machineAlias/v2`)
  },
  getFileContent(url) {
    return axios.get(url, {
      withCredentials: false,
    })
  },
  getShieldConfig(machineId) {
    return axios.get(
      `${apiServer}/detection/machine/${machineId}/shield-config`,
    )
  },
  putShieldConfig(machineId, cameraId, data) {
    return axios.put(
      `${apiServer}/detection/machine/${machineId}/shield-config/camera/${cameraId}`,
      data,
    )
  },
  getShieldLineConfig(machineId, cameraId) {
    return new Promise((resolve, reject) => {
      Promise.all([
        axios.get(`${apiServer}/syncedFile/file/${machineId}_config.yaml`),
        axios.get(
          `${apiServer}/detection/machine/${machineId}/blind-info-config`,
        ),
      ])
        .then(res => {
          const cameraName = `camera${String(cameraId).padStart(2, '0')}`
          const blindIdxList =
            JSON.parse(res[0].data.Content).cameras.find(
              item => item.name === cameraName,
            ).blind || []
          const timeBlindList = (res[1].data.blindInfoItems || []).filter(
            item => item.cameraName === cameraName,
          )

          blindIdxList.map(idx => {
            if (
              timeBlindList.findIndex(item => item.blindIndex === idx) === -1
            ) {
              timeBlindList.push({
                blindIndex: idx,
                cameraName: cameraName,
                isPermanent: true,
                site: '<unknown>',
                username: '<unknown>',
              })
            }
          })
          resolve(timeBlindList)
        })
        .catch(reject)
    })
  },
  putShieldLineConfig(machineId, cameraId, blindData) {
    const cameraName = `camera${String(cameraId).padStart(2, '0')}`
    return new Promise((resolve, reject) => {
      Promise.all([
        axios.get(`${apiServer}/syncedFile/file/${machineId}_config.yaml`),
        axios.get(
          `${apiServer}/detection/machine/${machineId}/blind-info-config`,
        ),
      ])
        .then(res => {
          const config = JSON.parse(res[0].data.Content)
          const timeBlind = res[1].data.blindInfoItems || []
          const curIdx = config.cameras.findIndex(
            camera => camera.name === cameraName,
          )
          config.cameras[curIdx].blind = blindData.map(item => item.blindIndex)
          blindData.forEach(item => {
            const timeBlindIdx = timeBlind.findIndex(
              tblind =>
                tblind.cameraName === item.cameraName &&
                tblind.blindIndex === item.blindIndex,
            )
            if (timeBlindIdx === -1) {
              timeBlind.push(item)
            } else {
              timeBlind[timeBlindIdx] = item
            }
          })
          Promise.all([
            axios.post(`${apiServer}/syncedFile`, {
              FileKey: res[0].data.FileKey,
              Content: JSON.stringify(config),
              UnixTimestamp: parseInt(+new Date() / 1e3),
            }),
            axios.put(
              `${apiServer}/detection/machine/${machineId}/blind-info-config`,
              { blindInfoItems: timeBlind },
            ),
          ])
            .then(resolve)
            .catch(reject)
        })
        .catch(reject)
    })
  },
  deleteLabel({ tarId, cameraId }) {
    return axios.delete(
      `${apiServer}/labeling/tars/${tarId}/cameras/${cameraId}/self`,
    )
  },
  log({
    type = 'default',
    priority = 'info',
    message = '',
    addr = '',
    user = '',
  }) {
    // use fetch to avoid network error report
    fetch(`${strApiServer}/logs`, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      redirect: 'follow',
      referrer: 'no-referrer',
      body: JSON.stringify({
        createTime: new Date().toISOString(),
        type,
        priority,
        message,
        addr,
        user,
      }),
    })
  },
  getUserInfo() {
    return axios.get(`${apiServer}/user/info`)
  },
  postConcat(params) {
    return axios.post(
      `${apiServer}/detectionData/tar-recombine/by-frame-ranges`,
      params,
    )
  },
  restoreTarFile({ tarUrl }) {
    return axios.get(`${decoderServer}/sync-tar?tar-file=${tarUrl}`, {
      withCredentials: false,
    })
  },
}

axios.interceptors.response.use(
  response => {
    return response
  },
  error => {
    const {
      message,
      config: { url },
      response: { data },
    } = error
    const dataStr = JSON.stringify(data) // 有些返回是object
    if (!dataStr.includes('file not found') && !url.includes('sync-tar')) {
      // 另外处理数据包缺失
      MessageBox.alert(
        `<strong>URL:</strong> ${url}<br><strong>错误:</strong> ${message}`,
        '请求出错',
        {
          confirmButtonText: '确定',
          callback: action => {},
          dangerouslyUseHTMLString: true,
        },
      )
    }
    // ANALYSE:
    api.log({
      type: 'label:networkError',
      priority: 'error',
      message: JSON.stringify(error),
      url: window.location.href,
      user: storejs.get('user').UserName,
    })

    return Promise.reject(error)
  },
)

export default api
